import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function AdminTicketDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => api.get(`/admin/tickets/${id}`).then((r) => setT(r.data));
  useEffect(() => { load(); }, [id]);

  const send = async (e) => { e.preventDefault(); await api.post(`/admin/tickets/${id}/messages`, { message: msg }); setMsg(""); toast.success("Reply sent"); load(); };
  const patch = async (payload) => { await api.patch(`/admin/tickets/${id}`, payload); toast.success("Updated"); load(); };

  if (!t) return <div className="text-slate-400 font-mono text-xs uppercase tracking-widest">Loading…</div>;

  return (
    <div data-testid="admin-ticket-detail">
      <Link to="/admin/tickets" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" strokeWidth={1.75}/> Back</Link>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="border border-slate-200 rounded-md bg-white p-6">
            <div className="font-mono text-xs text-slate-400">{t.ticket_id}</div>
            <h1 className="mt-1 font-display text-2xl font-semibold text-slate-900">{t.subject}</h1>
            <div className="text-xs text-slate-500 mt-1">{t.customer_name} · {t.customer_email}</div>
          </div>

          {t.messages.map((m) => (
            <div key={m.message_id} className={`border rounded-md p-4 ${m.author === "admin" ? "border-blue-200 bg-blue-50/40" : "border-slate-200 bg-white"}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-slate-900">{m.author_name} <span className="text-xs text-slate-500">· {m.author === "admin" ? "You (admin)" : "Customer"}</span></div>
                <div className="text-xs text-slate-500">{new Date(m.at).toLocaleString()}</div>
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{m.body}</div>
            </div>
          ))}

          {t.status !== "Closed" && (
            <form onSubmit={send} className="border border-slate-200 rounded-md bg-white p-4 space-y-2">
              <Textarea rows={4} required placeholder="Reply as support…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="admin-ticket-reply-input"/>
              <div className="flex justify-end">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="admin-ticket-reply-send">Send reply</Button>
              </div>
            </form>
          )}
        </div>

        <div className="space-y-4">
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="eyebrow mb-3">Status</div>
            <select value={t.status} onChange={(e) => patch({ status: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm">
              <option>Open</option><option>Waiting on Customer</option><option>Closed</option>
            </select>
          </div>
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="eyebrow mb-3">Priority</div>
            <select value={t.priority} onChange={(e) => patch({ priority: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm">
              <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
            </select>
          </div>
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="eyebrow mb-3">Category</div>
            <select value={t.category} onChange={(e) => patch({ category: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm">
              <option>General</option><option>Order</option><option>Documentation</option><option>Billing</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
